/**
 * @module utils/crow
 */
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import logger from "utils/logger";
import tgamConfig from "config/tgam";

const mightBePhantomJsBrowser = (() => {
  if (window.callPhantom || window._phantom || /phantomjs/gim.test(window.navigator.userAgent.toLowerCase())) {
    return true;
  }
  return false;
})();

const debugTag = "[CROW]";

/*
 * @method wrapMethod
 * @description Wrap function and return un-executed wrapped function.
 * @param {Object} tags - A specific set of tags for this context
 * @param {Function} fn - The function to be wrapped
 * @returns {Function} The newly wrapped function
 */
function wrapMethod(tags, fn) {
  if (isNil(tags) || !isFunction(fn)) {
    return fn;
  }
  // don't wrap function more than once
  if (!fn.__wrapped__) {
    fn.__wrapped__ = function _wrapped() {
      try {
        return fn();
      } catch (e) {
        captureException(e, tags); // report the error
        throw e; // re-throw the error
      }
    };
  }
  return fn.__wrapped__;
}

/**
 * @method contextMethod
 * @description Wrap function and then execute it now.
 * @param {Object} tags - A specific set of tags for this context
 * @param {Function} fn - The function to be wrapped
 * @returns {Function} The result of newly wrapped function
 */
function contextMethod(tags, fn) {
  return wrapMethod(tags, fn)();
}

/**
 * @method captureMessage
 * @param {String} msg - A plain message to be captured
 */
function captureMessage(msg) {
  if (mightBePhantomJsBrowser) {
    return;
  }
  logger.info(`${debugTag} message:`, msg);
  if (tgamConfig.sentry.isInstalled) {
    window.Sentry.captureMessage(msg);
  } else if (tgamConfig.sentry.doInstall) {
    window.tgam.sentry.messages.push(msg);
  }
}

/**
 * @method captureException
 * @param {Error} err - An exception to be logged
 * @param {Object} [tags] - A specific set of tags for this error [optional]
 */
function captureException(err, tags) {
  if (mightBePhantomJsBrowser) {
    return;
  }
  logger.warn(`${debugTag} exception:`, tags, err);
  if (tgamConfig.sentry.isInstalled) {
    window.Sentry.captureException(err, {
      tags: tags || {}
    });
  } else if (tgamConfig.sentry.doInstall) {
    window.tgam.sentry.errors.push({
      err: err,
      tags: tags || {}
    });
  }
}

// true when in client-side browser environment.
// false when in server-side CircleCI environment.
const inBrowser = !!(window && window.tgam && window.tgam.sentry);

// We don't want to capture errors during automated testing.
// So return dummy methods when not in a client-side browser.
export default (inBrowser ? {
  wrap: wrapMethod,
  context: contextMethod,
  message: captureMessage,
  error: captureException
} : {
  // options are discarded in below methods
  wrap: (opts, fn) => fn,
  context: (opts, fn) => fn(),
  message: () => {}, // noop
  error: () => {} // noop
});
